import { Add, Delete, Edit, Restore } from "@mui/icons-material";
import {
  Alert,
  Box,
  Checkbox,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  createCalendarOverride,
  deleteCalendarOverride,
  updateCalendarOverride,
} from "api";
import { getTranslation } from "common";
import ConfirmDialog from "components/dialogs/ConfirmDialog";
import FlowentiButton from "components/flowenti-ui-kit/FlowentiButton";
import { useTranslations } from "hooks";
import type {
  CalendarMutationResult,
  CalendarOverride,
  CalendarOverrideCreate,
  CalendarOverridePatch,
  EffectiveCalendarDay,
  OrganizationCalendar,
  OverrideInterval,
} from "model/organizationCalendarModel";
import React, { useState } from "react";
import CalendarFieldLabel, {
  calendarDescriptionId,
} from "./CalendarFieldLabel";
import { validateIntervals } from "./calendarValidation";
import CalendarPickerField from "./CalendarPickerField";

interface OverrideDraft {
  id?: number;
  date: string;
  isWorkingDay: boolean;
  description: string;
  inheritWeekly: boolean;
  workingIntervals: OverrideInterval[];
  original?: CalendarOverride;
}

interface CalendarOverridesEditorProps {
  organizationId: number;
  calendar: OrganizationCalendar;
  year: string;
  onYearChange: (year: string) => void;
  disabled?: boolean;
  runMutation: (
    operation: () => Promise<CalendarMutationResult>
  ) => Promise<boolean>;
}

const copyIntervals = (intervals: OverrideInterval[]) =>
  intervals.map((interval) => ({ ...interval }));

const sameIntervals = (
  left: OverrideInterval[],
  right: OverrideInterval[]
) => JSON.stringify(left) === JSON.stringify(right);

const CalendarOverridesEditor: React.FC<CalendarOverridesEditorProps> = ({
  organizationId,
  calendar,
  year,
  onYearChange,
  disabled = false,
  runMutation,
}) => {
  const translations = useTranslations();
  const [draft, setDraft] = useState<OverrideDraft | null>(null);
  const [saveFailed, setSaveFailed] = useState(false);
  const [deleteDay, setDeleteDay] = useState<EffectiveCalendarDay | null>(null);
  const closeEditor = () => {
    setDraft(null);
    setSaveFailed(false);
  };

  const listedDays = calendar.days.filter(
    (day) => day.holidays.length > 0 || day.override !== null
  );
  const normalizedIntervals = draft
    ? !draft.isWorkingDay || draft.inheritWeekly
      ? []
      : draft.workingIntervals.map(({ startsAt, endsAt }) => ({
          startsAt,
          endsAt,
        }))
    : [];
  const intervalErrors = draft
    ? validateIntervals(normalizedIntervals, "override")
    : [];
  const customHoursRequired = Boolean(
    draft?.isWorkingDay &&
      !draft.inheritWeekly &&
      normalizedIntervals.length === 0
  );

  const editDay = (day: EffectiveCalendarDay) => {
    if (day.override) {
      setDraft({
        id: day.override.id,
        date: day.date,
        isWorkingDay: day.override.isWorkingDay,
        description: day.override.description,
        inheritWeekly:
          day.override.isWorkingDay &&
          day.override.workingIntervals.length === 0,
        workingIntervals: copyIntervals(day.override.workingIntervals),
        original: {
          ...day.override,
          workingIntervals: copyIntervals(day.override.workingIntervals),
        },
      });
    }
  };

  const saveOverride = async () => {
    if (!draft || !draft.date || customHoursRequired || intervalErrors.length) {
      return;
    }

    setSaveFailed(false);
    let saved = false;
    if (!draft.original || draft.id === undefined) {
      const body: CalendarOverrideCreate = {
        calendarDate: draft.date,
        isWorkingDay: draft.isWorkingDay,
        ...(draft.description ? { description: draft.description } : {}),
        workingIntervals: normalizedIntervals,
        expectedCalendarRevision: calendar.calendarRevision,
      };
      saved = await runMutation(() =>
        createCalendarOverride(organizationId, body)
      );
    } else {
      const body: CalendarOverridePatch = {
        expectedCalendarRevision: calendar.calendarRevision,
        ...(draft.isWorkingDay !== draft.original.isWorkingDay
          ? { isWorkingDay: draft.isWorkingDay }
          : {}),
        ...(draft.description !== draft.original.description
          ? { description: draft.description }
          : {}),
        ...(!sameIntervals(
          normalizedIntervals,
          draft.original.workingIntervals
        )
          ? { workingIntervals: normalizedIntervals }
          : {}),
      };
      if (Object.keys(body).length === 1) return;
      saved = await runMutation(() =>
        updateCalendarOverride(organizationId, draft.id!, body)
      );
    }

    if (saved) closeEditor();
    else setSaveFailed(true);
  };

  const restoreRule = async (day: EffectiveCalendarDay) => {
    if (!day.override) return;
    const saved = await runMutation(() =>
      deleteCalendarOverride(
        organizationId,
        day.override!.id,
        calendar.calendarRevision
      )
    );
    if (saved) setDraft(null);
  };

  const removeHoliday = async (day: EffectiveCalendarDay) => {
    if (disabled) return;
    if (!day.holidays.length) {
      await restoreRule(day);
      return;
    }
    const body = {
      isWorkingDay: true,
      description: "",
      workingIntervals: [],
      expectedCalendarRevision: calendar.calendarRevision,
    };
    const saved = await runMutation(() =>
      day.override
        ? updateCalendarOverride(organizationId, day.override.id, body)
        : createCalendarOverride(organizationId, {
            ...body,
            calendarDate: day.date,
          })
    );
    if (saved) setDraft(null);
  };

  const updateDraftInterval = (
    index: number,
    patch: Partial<OverrideInterval>
  ) =>
    setDraft((current) =>
      current
        ? {
            ...current,
            workingIntervals: current.workingIntervals.map(
              (interval, intervalIndex) =>
                intervalIndex === index
                  ? { ...interval, ...patch }
                  : interval
            ),
          }
        : current
    );

  const yearFieldId = "year";

  return (
    <Stack spacing={2}>
      <Typography variant="subtitle1" fontWeight={700}>
        {getTranslation(translations, "calendar.holidays.title")}
      </Typography>
      <Box sx={{ maxWidth: 180 }}>
        <CalendarFieldLabel
          fieldId={yearFieldId}
          label={getTranslation(translations, "calendar.year.label")}
          help={getTranslation(translations, "calendar.year.help")}
        />
        <TextField
          id="calendar-year"
          fullWidth
          size="small"
          type="number"
          value={year}
          disabled={disabled}
          onChange={(event) => onYearChange(event.target.value)}
          inputProps={{
            min: 1,
            max: 9999,
            "aria-label": getTranslation(
              translations,
              "calendar.year.label"
            ),
            "aria-describedby": calendarDescriptionId(yearFieldId),
            "data-calendar-field": "true",
            "data-calendar-field-id": yearFieldId,
          }}
        />
      </Box>

      <FlowentiButton
        size="small"
        variant="outlined"
        startIcon={<Add />}
        disabled={disabled}
        data-testid="calendar-override-add"
        onClick={() =>
          setDraft({
            date: "",
            isWorkingDay: false,
            description: "",
            inheritWeekly: true,
            workingIntervals: [],
          })
        }
      >
        {getTranslation(translations, "calendar.override.add")}
      </FlowentiButton>

      {listedDays.map((day) => (
        <Stack
          key={day.date}
          data-testid={`calendar-day-${day.date}`}
          spacing={1}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            p: 2,
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ sm: "center" }}
            spacing={1}
          >
            <Typography fontWeight={700}>{day.date}</Typography>
            {day.holidays.length > 0 && (
              <Chip
                size="small"
                data-testid="calendar-holiday-system"
                label={getTranslation(
                  translations,
                  "calendar.holiday.organization"
                )}
              />
            )}
            {day.holidays.some((holiday) => holiday.observed) && (
              <Chip
                size="small"
                data-testid="calendar-holiday-observed"
                label={getTranslation(
                  translations,
                  "calendar.holiday.observed"
                )}
              />
            )}
            {day.override && (
              <Chip
                size="small"
                color="primary"
                label={getTranslation(
                  translations,
                  "calendar.override.label"
                )}
              />
            )}
            {day.holidays.length > 0 && day.override?.isWorkingDay &&
              day.override.workingIntervals.length === 0 && (
                <Chip
                  size="small"
                  data-testid="calendar-holiday-excluded"
                  label={getTranslation(translations, "calendar.holiday.excluded")}
                />
              )}
          </Stack>
          {day.holidays.map((holiday, index) => (
            <Typography key={`${holiday.name}-${index}`} variant="body2">
              {holiday.name}
            </Typography>
          ))}
          {day.override?.description && (
            <Typography variant="body2">
              {day.override.description}
            </Typography>
          )}
          <Stack direction="row" spacing={1}>
            {day.override && (
            <FlowentiButton
              size="small"
              variant="outlined"
              startIcon={<Edit />}
              disabled={disabled}
              data-testid={`calendar-day-${day.date}-edit`}
              onClick={() => editDay(day)}
            >
              {getTranslation(
                translations,
                "calendar.override.edit"
              )}
            </FlowentiButton>
            )}
            <FlowentiButton
              size="small"
              variant="outlined"
              startIcon={<Delete />}
              disabled={disabled || Boolean(
                day.holidays.length && day.override?.isWorkingDay &&
                day.override.workingIntervals.length === 0
              )}
              data-testid={`calendar-day-${day.date}-delete`}
              onClick={() => setDeleteDay(day)}
            >
              {getTranslation(translations, "calendar.holiday.delete")}
            </FlowentiButton>
            {day.override && (
              <FlowentiButton
                size="small"
                variant="outlined"
                startIcon={<Restore />}
                disabled={disabled}
                data-testid={`calendar-day-${day.date}-restore`}
                onClick={() => void restoreRule(day)}
              >
                {getTranslation(
                  translations,
                  day.holidays.length
                    ? "calendar.override.restore_organization"
                    : "calendar.override.restore_weekly"
                )}
              </FlowentiButton>
            )}
          </Stack>
        </Stack>
      ))}

      <ConfirmDialog
        open={deleteDay !== null}
        onClose={() => setDeleteDay(null)}
        title={getTranslation(translations, "calendar.holiday.delete")}
        content={<>{deleteDay?.date}<br />{getTranslation(translations, "calendar.holiday.delete.confirm")}</>}
        cancelText={getTranslation(translations, "user.buttoncancel.label")}
        confirmText={getTranslation(translations, "calendar.holiday.delete")}
        onConfirm={() => {
          if (deleteDay) void removeHoliday(deleteDay);
          setDeleteDay(null);
        }}
      />

      {draft && (
        <Dialog
          open
          fullWidth
          maxWidth="sm"
          data-testid="calendar-override-dialog"
          aria-labelledby="calendar-override-dialog-title"
          onClose={() => { if (!disabled || saveFailed) closeEditor(); }}
        >
          <DialogTitle id="calendar-override-dialog-title">
            {getTranslation(translations, draft.id === undefined
              ? "calendar.override.add" : "calendar.override.edit")}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
          <Box>
            <CalendarFieldLabel
              fieldId="override-date"
              label={getTranslation(
                translations,
                "calendar.override.date.label"
              )}
              help={getTranslation(
                translations,
                "calendar.override.date.help"
              )}
            />
            <CalendarPickerField
              id="calendar-override-date"
              size="small"
              type="date"
              value={draft.date}
              disabled={disabled || draft.id !== undefined}
              onChange={(value) =>
                setDraft((current) =>
                  current ? { ...current, date: value } : current
                )
              }
              inputProps={{
                "aria-label": getTranslation(
                  translations,
                  "calendar.override.date.label"
                ),
                "aria-describedby":
                  calendarDescriptionId("override-date"),
                "data-calendar-field": "true",
                "data-calendar-field-id": "override-date",
              }}
            />
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={draft.isWorkingDay}
                disabled={disabled}
                onChange={(_, checked) =>
                  setDraft((current) =>
                    current
                      ? {
                          ...current,
                          isWorkingDay: checked,
                          inheritWeekly: checked
                            ? current.inheritWeekly ||
                              current.workingIntervals.length === 0
                            : current.inheritWeekly,
                        }
                      : current
                  )
                }
                inputProps={{
                  "aria-label": getTranslation(
                    translations,
                    "calendar.override.working_day.label"
                  ),
                  "aria-describedby": calendarDescriptionId(
                    "override-working-day"
                  ),
                  "data-calendar-field": "true",
                  "data-calendar-field-id": "override-working-day",
                  "data-testid": "calendar-override-working-day",
                } as React.InputHTMLAttributes<HTMLInputElement>}
              />
            }
            label={
              <CalendarFieldLabel
                fieldId="override-working-day"
                label={getTranslation(
                  translations,
                  "calendar.override.working_day.label"
                )}
                help={getTranslation(
                  translations,
                  "calendar.override.working_day.help"
                )}
              />
            }
          />
          {draft.isWorkingDay && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={draft.inheritWeekly}
                  disabled={disabled}
                  onChange={(_, checked) =>
                    setDraft((current) =>
                      current
                        ? {
                            ...current,
                            inheritWeekly: checked,
                            workingIntervals:
                              !checked &&
                              current.workingIntervals.length === 0
                                ? [{ startsAt: "09:00", endsAt: "17:00" }]
                                : current.workingIntervals,
                          }
                        : current
                    )
                  }
                  inputProps={{
                    "aria-label": getTranslation(
                      translations,
                      "calendar.override.inherit.label"
                    ),
                    "aria-describedby": calendarDescriptionId(
                      "override-inherit"
                    ),
                    "data-calendar-field": "true",
                    "data-calendar-field-id": "override-inherit",
                    "data-testid": "calendar-override-inherit-weekly",
                  } as React.InputHTMLAttributes<HTMLInputElement>}
                />
              }
              label={
                <CalendarFieldLabel
                  fieldId="override-inherit"
                  label={getTranslation(
                    translations,
                    "calendar.override.inherit.label"
                  )}
                  help={getTranslation(
                    translations,
                    "calendar.override.inherit.help"
                  )}
                />
              }
            />
          )}
          <Box>
            <CalendarFieldLabel
              fieldId="override-description"
              label={getTranslation(
                translations,
                "calendar.override.description.label"
              )}
              help={getTranslation(
                translations,
                "calendar.override.description.help"
              )}
            />
            <TextField
              id="calendar-override-description"
              fullWidth
              size="small"
              value={draft.description}
              disabled={disabled}
              onChange={(event) =>
                setDraft((current) =>
                  current
                    ? { ...current, description: event.target.value }
                    : current
                )
              }
              inputProps={{
                maxLength: 255,
                "aria-label": getTranslation(
                  translations,
                  "calendar.override.description.label"
                ),
                "aria-describedby": calendarDescriptionId(
                  "override-description"
                ),
                "data-calendar-field": "true",
                "data-calendar-field-id": "override-description",
              }}
            />
          </Box>

          {draft.isWorkingDay &&
            !draft.inheritWeekly &&
            draft.workingIntervals.map((interval, index) => {
              const error = intervalErrors.find(
                (item) => item.key === `override-${index}`
              );
              const startFieldId = `override-interval-${index}-start`;
              const endFieldId = `override-interval-${index}-end`;
              return (
                <Stack
                  key={index}
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  alignItems={{ sm: "center" }}
                >
                  <Box sx={{ flex: 1 }}>
                    <CalendarFieldLabel
                      fieldId={startFieldId}
                      label={getTranslation(
                        translations,
                        "calendar.interval.start.label"
                      )}
                      help={getTranslation(
                        translations,
                        "calendar.interval.start.help"
                      )}
                    />
                    <CalendarPickerField
                      fullWidth
                      size="small"
                      type="time"
                      value={interval.startsAt}
                      disabled={disabled}
                      error={Boolean(error)}
                      onChange={(value) =>
                        updateDraftInterval(index, {
                          startsAt: value,
                        })
                      }
                      inputProps={{
                        step: 60,
                        "aria-label": getTranslation(
                          translations,
                          "calendar.interval.start.label"
                        ),
                        "aria-describedby":
                          calendarDescriptionId(startFieldId),
                        "data-calendar-field": "true",
                        "data-calendar-field-id": startFieldId,
                        "data-testid": `calendar-override-interval-${index}-start`,
                      }}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <CalendarFieldLabel
                      fieldId={endFieldId}
                      label={getTranslation(
                        translations,
                        "calendar.interval.end.label"
                      )}
                      help={getTranslation(
                        translations,
                        "calendar.interval.end.help"
                      )}
                    />
                    <CalendarPickerField
                      fullWidth
                      size="small"
                      type="time"
                      value={interval.endsAt}
                      disabled={disabled}
                      error={Boolean(error)}
                      helperText={
                        error
                          ? getTranslation(translations, error.messageKey)
                          : undefined
                      }
                      onChange={(value) =>
                        updateDraftInterval(index, {
                          endsAt: value,
                        })
                      }
                      inputProps={{
                        step: 60,
                        "aria-label": getTranslation(
                          translations,
                          "calendar.interval.end.label"
                        ),
                        "aria-describedby":
                          calendarDescriptionId(endFieldId),
                        "data-calendar-field": "true",
                        "data-calendar-field-id": endFieldId,
                        "data-testid": `calendar-override-interval-${index}-end`,
                      }}
                    />
                  </Box>
                  <IconButton
                    disabled={disabled}
                    data-testid={`calendar-override-interval-${index}-remove`}
                    aria-label={getTranslation(
                      translations,
                      "calendar.interval.remove"
                    )}
                    onClick={() =>
                      setDraft((current) =>
                        current
                          ? {
                              ...current,
                              workingIntervals:
                                current.workingIntervals.filter(
                                  (_, itemIndex) => itemIndex !== index
                                ),
                            }
                          : current
                      )
                    }
                  >
                    <Delete />
                  </IconButton>
                </Stack>
              );
            })}

          {draft.isWorkingDay && !draft.inheritWeekly && (
            <FlowentiButton
              size="small"
              variant="outlined"
              startIcon={<Add />}
              disabled={disabled}
              data-testid="calendar-override-interval-add"
              onClick={() =>
                setDraft((current) =>
                  current
                    ? {
                        ...current,
                        workingIntervals: [
                          ...current.workingIntervals,
                          { startsAt: "09:00", endsAt: "17:00" },
                        ],
                      }
                    : current
                )
              }
            >
              {getTranslation(translations, "calendar.interval.add")}
            </FlowentiButton>
          )}

          {(customHoursRequired || intervalErrors.length > 0) && (
            <Alert severity="error" data-testid="calendar-override-validation-errors">
              {getTranslation(
                translations,
                customHoursRequired
                  ? "calendar.interval.error.required"
                  : intervalErrors[0].messageKey
              )}
            </Alert>
          )}
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <FlowentiButton
              variant="outlined"
              disabled={disabled && !saveFailed}
              onClick={closeEditor}
            >
              {getTranslation(translations, "user.buttoncancel.label")}
            </FlowentiButton>
            <FlowentiButton
              variant="contained"
              disabled={
                disabled ||
                !draft.date ||
                customHoursRequired ||
                intervalErrors.length > 0
              }
              data-testid="calendar-override-save"
              onClick={() => void saveOverride()}
            >
              {getTranslation(translations, "pm.button.save")}
            </FlowentiButton>
          </Stack>
          {saveFailed && <Alert severity="error">{getTranslation(translations, "calendar.settings.save_error")}</Alert>}
            </Stack>
          </DialogContent>
        </Dialog>
      )}
    </Stack>
  );
};

export default CalendarOverridesEditor;
